
import { useState, useEffect } from "react";
import { Verified } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TrustBadge from "@/components/trust/TrustBadge";
import { useTrust } from "@/hooks/useTrust";

interface GprofileHeroProps {
  profile: {
    user_id: string;
    first_name: string | null;
    display_name: string | null;
    age: number | null;
    photo_urls: string[] | null;
    public_interests: string[] | null;
    is_online?: boolean;
    last_seen?: string;
    distance?: number | null;
    is_verified?: boolean;
  };
  currentPhotoIndex: number;
  onPhotoChange: (index: number) => void;
}

const GprofileHero = ({ profile, currentPhotoIndex, onPhotoChange }: GprofileHeroProps) => {
  const [fullScreenPhoto, setFullScreenPhoto] = useState<string | null>(null);
  const { trustStats } = useTrust(profile.user_id);

  const photos = profile.photo_urls || [];
  const hasMultiplePhotos = photos.length > 1;

  const getDisplayName = () => {
    return profile.display_name || profile.first_name || 'User';
  };

  const getStatusText = () => {
    if (profile.is_online) return "Online now";
    
    if (profile.last_seen) {
      const lastSeen = new Date(profile.last_seen);
      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60));
      
      if (diffMinutes < 60) {
        return `Seen ${diffMinutes} minutes ago`;
      } else if (diffMinutes < 1440) {
        const hours = Math.floor(diffMinutes / 60);
        return `Seen ${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else {
        const days = Math.floor(diffMinutes / 1440);
        return `Seen ${days} day${days > 1 ? 's' : ''} ago`;
      }
    }
    
    return "Last seen recently";
  };

  const getStatusColor = () => {
    if (profile.is_online) return "bg-toxic-green";
    
    if (profile.last_seen) {
      const lastSeen = new Date(profile.last_seen);
      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60));
      
      if (diffMinutes < 60) return "bg-toxic-green";
      if (diffMinutes < 1440) return "bg-neon-red";
    }
    
    return "bg-ice-white/40";
  };

  const getDistanceText = () => {
    if (profile.distance === null || profile.distance === undefined) {
      return "Distance hidden";
    }
    
    if (profile.distance < 1) {
      return `${Math.round(profile.distance * 1000)}m away`;
    }
    
    return `${profile.distance.toFixed(1)}km away`;
  };

  // Swipe gesture handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    
    if (Math.abs(distance) < minSwipeDistance) return;
    
    if (distance > minSwipeDistance) {
      // Swiped left - next photo
      handleNextPhoto();
    } else if (distance < -minSwipeDistance) {
      // Swiped right - previous photo  
      handlePrevPhoto();
    }
  };

  const handlePrevPhoto = () => {
    if (photos.length > 1) {
      const newIndex = currentPhotoIndex === 0 ? photos.length - 1 : currentPhotoIndex - 1;
      onPhotoChange(newIndex);
    }
  };

  const handleNextPhoto = () => {
    if (photos.length > 1) {
      const newIndex = currentPhotoIndex === photos.length - 1 ? 0 : currentPhotoIndex + 1;
      onPhotoChange(newIndex);
    }
  };

  const handleDotClick = (index: number) => {
    onPhotoChange(index);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevPhoto();
      } else if (e.key === 'ArrowRight') {
        handleNextPhoto();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPhotoIndex, photos.length]);

  const getTagColor = (tag: string) => {
    const colors = [
      "bg-neon-red/20 text-neon-red border-neon-red/30",
      "bg-toxic-green/20 text-toxic-green border-toxic-green/30",
      "bg-electric-violet/20 text-electric-violet border-electric-violet/30",
      "bg-ice-white/20 text-ice-white border-ice-white/30",
    ];
    return colors[tag.length % colors.length];
  };

  return (
    <>
      <div className="relative mt-20">
        {/* Main Photo with Swipe Support */}
        <div 
          className="relative aspect-[3/4] w-full overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {photos.length > 0 ? (
            <img
              src={photos[currentPhotoIndex]}
              alt={getDisplayName()}
              className="w-full h-full object-cover cursor-pointer transition-opacity duration-300"
              onClick={() => setFullScreenPhoto(photos[currentPhotoIndex])}
            />
          ) : (
            <div className="w-full h-full bg-carbon/80 flex items-center justify-center">
              <div className="w-24 h-24 bg-ice-white/20 rounded-full flex items-center justify-center">
                <span className="text-4xl">👤</span>
              </div>
            </div>
          )}

          {/* Invisible Touch Zones for Navigation */}
          {hasMultiplePhotos && (
            <>
              {/* Left touch zone */}
              <button
                onClick={handlePrevPhoto}
                className="absolute left-0 top-0 w-1/3 h-full z-10 focus:outline-none"
                aria-label="Previous photo"
              />
              {/* Right touch zone */}
              <button
                onClick={handleNextPhoto}
                className="absolute right-0 top-0 w-1/3 h-full z-10 focus:outline-none"
                aria-label="Next photo"
              />
            </>
          )}

          {/* Enhanced Dot Indicators */}
          {hasMultiplePhotos && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-20 bg-black/60 backdrop-blur-sm px-2 py-3 rounded-full">
              {photos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`relative p-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-alpha-orange/50 rounded-full ${
                    index === currentPhotoIndex ? 'scale-110' : 'hover:scale-105'
                  }`}
                  aria-label={`Go to photo ${index + 1}`}
                >
                  <div
                    className={`w-4 h-4 rounded-full transition-all duration-300 ${
                      index === currentPhotoIndex 
                        ? 'bg-alpha-orange shadow-[0_0_12px_rgba(255,95,31,0.8)]' 
                        : 'bg-ice-white/50 hover:bg-ice-white/80'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Enhanced Photo Counter */}
          {hasMultiplePhotos && (
            <div className="absolute top-4 right-4 z-30">
              <div className="bg-black/80 border border-alpha-orange/30 backdrop-blur-sm px-3 py-1 rounded-full">
                <span className="text-ice-white text-sm font-medium">
                  {currentPhotoIndex + 1}/{photos.length}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Profile Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-carbon via-carbon/80 to-transparent p-4">
          {/* Name, Age, Verification, Trust Badge */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h1 className="text-3xl font-bold text-ice-white">
              {getDisplayName()}
              {profile.age && <span className="text-2xl font-normal ml-2 text-toxic-green">{profile.age}</span>}
            </h1>
            <div className="flex items-center gap-2">
              {profile.is_verified && (
                <Verified className="w-6 h-6 text-toxic-green fill-current" />
              )}
              {trustStats && (
                <TrustBadge 
                  trustedCount={trustStats.trusted_count} 
                  isTrusted={trustStats.is_trusted} 
                  variant="icon-only"
                />
              )}
            </div>
          </div>

          {/* Distance and Status */}
          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor()}`} />
              <span className="text-ice-white/80 text-sm">{getStatusText()}</span>
            </div>
            <span className="text-ice-white/80 text-sm">
              {getDistanceText()}
            </span>
          </div>

          {/* Tags/Tribes */}
          {profile.public_interests && profile.public_interests.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {profile.public_interests.slice(0, 6).map((interest, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className={`text-xs px-2 py-1 border ${getTagColor(interest)}`}
                >
                  #{interest}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full Screen Photo Modal */}
      {fullScreenPhoto && (
        <div
          className="fixed inset-0 z-50 bg-carbon flex items-center justify-center"
          onClick={() => setFullScreenPhoto(null)}
        >
          <img
            src={fullScreenPhoto}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
          />
          <button
            className="absolute top-4 right-4 text-ice-white hover:bg-ice-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-colors text-2xl font-light"
            onClick={() => setFullScreenPhoto(null)}
            aria-label="Close full screen view"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default GprofileHero;
